import { FileType, UploadStatus } from '../interfaces/upload/IUpload';
import { UploadProgressStepState } from '../interfaces/upload/upload-progress.interface';
import { UploadStepperStep } from '../interfaces/upload/upload-stepper-step.interface';

export class UploadProgressOperations {
  private static readonly terminalStatuses: UploadStatus[] = [
    UploadStatus.DATA_PARSED,
    UploadStatus.PARSING_FAILED,
    UploadStatus.AV_SCAN_FAILED,
    UploadStatus.USER_CANCELLED,
  ];

  private static readonly errorStatuses: UploadStatus[] = [UploadStatus.PARSING_FAILED, UploadStatus.AV_SCAN_FAILED];

  private static readonly stepperSteps: UploadStepperStep[] = [
    {
      id: 'prepare',
      labelKey: 'uploadProgress.steps.prepare',
      completedStatuses: [
        UploadStatus.UPLOADED,
        UploadStatus.AV_SCAN_DONE,
        UploadStatus.DATA_PARSED,
        UploadStatus.PARSING_FAILED,
        UploadStatus.AV_SCAN_FAILED,
        UploadStatus.USER_CANCELLED,
      ],
      currentStatuses: [UploadStatus.READY_FOR_UPLOAD],
    },
    {
      id: 'uploaded',
      labelKey: 'uploadProgress.steps.uploaded',
      completedStatuses: [UploadStatus.AV_SCAN_DONE, UploadStatus.DATA_PARSED, UploadStatus.PARSING_FAILED],
      currentStatuses: [UploadStatus.UPLOADED, UploadStatus.AV_SCAN_FAILED, UploadStatus.USER_CANCELLED],
    },
    {
      id: 'scanned',
      labelKey: 'uploadProgress.steps.scanned',
      completedStatuses: [UploadStatus.DATA_PARSED, UploadStatus.PARSING_FAILED],
      currentStatuses: [UploadStatus.AV_SCAN_DONE],
    },
    {
      id: 'processed',
      labelKey: 'uploadProgress.steps.processed',
      completedStatuses: [UploadStatus.DATA_PARSED],
      currentStatuses: [UploadStatus.PARSING_FAILED, UploadStatus.USER_CANCELLED],
    },
  ];

  public static isTerminalStatus(status: UploadStatus): boolean {
    return this.terminalStatuses.includes(status);
  }

  public static isErrorStatus(status: UploadStatus): boolean {
    return this.errorStatuses.includes(status);
  }

  public static canTransition(currentStatus: UploadStatus, nextStatus: UploadStatus): boolean {
    if (currentStatus === nextStatus) {
      return false;
    }

    if (this.isTerminalStatus(currentStatus)) {
      return false;
    }

    if (this.isTerminalStatus(nextStatus)) {
      return true;
    }

    return this.getStatusOrder(nextStatus) >= this.getStatusOrder(currentStatus);
  }

  public static getStatusLabelKey(status: UploadStatus): string {
    switch (status) {
      case UploadStatus.READY_FOR_UPLOAD:
        return 'uploadProgress.status.readyForUpload';
      case UploadStatus.UPLOADED:
        return 'uploadProgress.status.uploaded';
      case UploadStatus.AV_SCAN_DONE:
        return 'uploadProgress.status.avScanDone';
      case UploadStatus.AV_SCAN_FAILED:
        return 'uploadProgress.status.avScanFailed';
      case UploadStatus.DATA_PARSED:
        return 'uploadProgress.status.dataParsed';
      case UploadStatus.PARSING_FAILED:
        return 'uploadProgress.status.parsingFailed';
      case UploadStatus.USER_CANCELLED:
        return 'uploadProgress.status.userCancelled';
      default:
        return 'uploadProgress.status.unknown';
    }
  }

  public static getDefaultDisplayName(fileType: FileType): string {
    switch (fileType) {
      case FileType.BTAT:
        return 'uploadProgress.types.btat';
      case FileType.DAMEX:
        return 'uploadProgress.types.damex';
      case FileType.TRAVEL_RECEIPT:
        return 'uploadProgress.types.travelReceipt';
      case FileType.SAP_IMPORT:
        return 'uploadProgress.types.sapImport';
      case FileType.TRAVEL_MANAGEMENT:
        return 'uploadProgress.types.travelManagement';
      case FileType.OTHER_COSTS:
        return 'uploadProgress.types.otherCosts';
      case FileType.OUT_OF_OFFICE_ATTACHMENT:
        return 'uploadProgress.types.outOfOfficeAttachment';
      default:
        return 'uploadProgress.types.genericUpload';
    }
  }

  public static getStepStates(status: UploadStatus): UploadProgressStepState[] {
    return this.stepperSteps.map(step => ({
      id: step.id,
      labelKey: step.labelKey,
      cssClass: this.getStepClass(step, status),
    }));
  }

  private static getStepClass(step: UploadStepperStep, status: UploadStatus): string {
    if (this.isErrorStatus(status)) {
      if (step.currentStatuses.includes(status)) {
        return 'error current';
      }

      if (step.completedStatuses.includes(status)) {
        return 'done';
      }

      return '';
    }

    if (status === UploadStatus.USER_CANCELLED) {
      if (step.currentStatuses.includes(status)) {
        return 'cancelled current';
      }

      if (step.completedStatuses.includes(status)) {
        return 'done';
      }

      return '';
    }

    if (step.completedStatuses.includes(status)) {
      return 'done';
    }

    if (step.currentStatuses.includes(status)) {
      return 'current';
    }

    return '';
  }

  private static getStatusOrder(status: UploadStatus): number {
    switch (status) {
      case UploadStatus.READY_FOR_UPLOAD:
        return 0;
      case UploadStatus.UPLOADED:
        return 1;
      case UploadStatus.AV_SCAN_DONE:
        return 2;
      case UploadStatus.AV_SCAN_FAILED:
        return 2;
      case UploadStatus.DATA_PARSED:
      case UploadStatus.PARSING_FAILED:
      case UploadStatus.USER_CANCELLED:
        return 3;
      default:
        return -1;
    }
  }
}
